import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, Controller, useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { XIcon as CloseIcon } from 'lucide-react'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import SearchBar from 'components/SearchBar'
import { DropDownItem } from 'types/components'
import { VouchersDataTypes } from 'schemas/campaign-voucher-schemas'
import { postSearchVouchersAPI } from 'api/campaign-voucher'
import {
  ReportVoucherRedemptionCountCreateFormSchemas,
  ReportVoucherRedemptionCountCreateFormTypes,
} from 'schemas/reports-voucher-count-schemas'

import {
  TRANSACTION_REPORT_CALENDAR_UNIT,
} from 'constants/filters'
import { ROUTES } from 'constants/routing'
import { useReportVoucherRedemptionMutation } from './use-report-voucher-redemption-count'
import { ExportToCSV } from '../../utils-reports-actions'

export const CreateReportVoucherRedemptionForm = ({
  onCloseModal,
}: {
  onCloseModal: () => void
}) => {
  const resultRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const [queryString, queryStringSet] = useState<string | null>(null)

  const [voucherResults, setVoucherResults] = useState<VouchersDataTypes[]>([])
  const [voucherSearchError, setVoucherSearchError] = useState<string | null>(null)
  const [isVoucherSearching, setIsVoucherSearching] = useState(false)
  const [selectedVouchers, setSelectedVouchers] = useState<VouchersDataTypes[]>([])
  const [voucherLimitError, setVoucherLimitError] = useState<string | null>(null)

  const date = new Date().toISOString().split('T')[0]
  const formDefaultValues: ReportVoucherRedemptionCountCreateFormTypes = {
    voucherIds: null,
    voucherCodes: null,
    startDate: date,
    endDate: date,
    groupLevel: 1,
  }

  const form = useForm<ReportVoucherRedemptionCountCreateFormTypes>({
    resolver: zodResolver(ReportVoucherRedemptionCountCreateFormSchemas),
    defaultValues: formDefaultValues,
  })

  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  const {
    data: reportVoucherRdemptionData,
    mutateAsync: postReportVoucherRedemptionMutation,
    isSuccess,
    isError,
    isLoading,
  } = useReportVoucherRedemptionMutation()

  const handleView = useCallback(() => {
    navigate(
      ROUTES.ReportsView.path +
      `/?report-query=${queryString}&report-type=voucher_redemption`
    )
  }, [queryString, navigate])

  const onSubmit = useCallback(
    async (formData: ReportVoucherRedemptionCountCreateFormTypes) => {
      const formattedData = {
        ...formData,
        startDate: `${formData.startDate} 00:00:00`,
        endDate: formData.endDate
          ? `${formData.endDate} 23:59:59`
          : null,
      }

      const queryString = encodeURIComponent(JSON.stringify(formattedData))
      queryStringSet(queryString)
      postReportVoucherRedemptionMutation(formattedData)
    },
    [postReportVoucherRedemptionMutation, queryStringSet]
  )

  useEffect(() => {
    form.setValue(
      'voucherIds',
      selectedVouchers.map((v) => Number(v.voucherId))
    )
    // form.setValue(
    //   'voucherCodes',
    //   selectedVouchers.map((v) => String(v.voucherCode))
    // )
  }, [form, selectedVouchers])

  useEffect(() => {
    if (resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isSuccess, isError, resultRef])

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4 items-center">
        <p className="font-light tracking-wide mb-2">
          Search vouchers
        </p>
        <Controller
          name="voucherIds"
          control={form.control}
          render={({ field }) => (
            <>
              <SearchBar
                isVoucherLookup
                placeholder=""
                startLoading={() => {
                  setIsVoucherSearching(true)
                  setVoucherSearchError(null)
                  setVoucherResults([])
                }}
                stopLoading={() => setIsVoucherSearching(false)}
                startFetch={async (parsedValues) => {
                  try {
                    const vouchers = await postSearchVouchersAPI({
                      voucherId: null,
                      voucherCode: parsedValues.voucherCode || parsedValues.code || null,
                      voucherName: parsedValues.voucherName || parsedValues['name/PLU'] || parsedValues.name || null,
                      productSkus: parsedValues.productSkus || parsedValues.productSKU || null,
                      voucherTypeId: null,
                      startDate: parsedValues.startDate
                        ? `${parsedValues.startDate} 00:00:00`
                        : null,
                      // pSize: 50,
                      // pNumber: 1,
                    })
                    if (vouchers.length === 0) {
                      setVoucherSearchError('No results found')
                    } else {
                      setVoucherResults(vouchers)
                    }
                  } catch (err) {
                    setVoucherSearchError('No results found')
                  } finally {
                    setIsVoucherSearching(false)
                  }
                }}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                closeSearchForm={() => { }}
              />

              {voucherSearchError && (
                <div className="text-red-600 text-sm mt-1">{voucherSearchError}</div>
              )}
              {isVoucherSearching && (
                <div className="text-black text-sm mt-1">Loading...</div>
              )}
              {voucherResults.length > 0 && (
                <div
                  className="overflow-y-auto border rounded mt-2 mb-2"
                  style={{ maxHeight: 240 }}
                >
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left">Voucher ID</th>
                        <th className="px-2 py-1 text-left">Voucher Name</th>
                        <th className="px-2 py-1 text-left">Voucher Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voucherResults.map((v) => {
                        const isSelected = selectedVouchers.some(
                          (sel) => sel.voucherId === v.voucherId
                        )

                        return (
                          <tr
                            key={v.voucherId}
                            className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-100' : ''}`}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedVouchers(selectedVouchers.filter((s) => s.voucherId !== v.voucherId))
                                setVoucherLimitError(null)
                              } else {
                                if (selectedVouchers.length >= 5) {
                                  setVoucherLimitError('Maximum 5 vouchers allowed')
                                  return
                                }
                                setSelectedVouchers([...selectedVouchers, v])
                                setVoucherLimitError(null)
                              }
                            }}
                          >
                            <td className="px-2 py-1">{v.voucherId}</td>
                            <td className="px-2 py-1">{v.voucherName}</td>
                            <td className="px-2 py-1">{v.voucherCode}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        />
      </div>

      {selectedVouchers.length ? (
        <div className="mb-4">
          <p className="font-light tracking-wide">Selected Vouchers</p>
          <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
            {selectedVouchers.map((v) => (
              <span
                onClick={() => { setSelectedVouchers(selectedVouchers.filter((s) => s.voucherId !== v.voucherId)); setVoucherLimitError(null); }}
                key={v.voucherId}
                className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
              >
                {`${v.voucherId} ${v.voucherName}`}
                <CloseIcon className="w-3 h-3" />
              </span>
            ))}
          </div>
          {voucherLimitError && (
            <div className="text-red text-sm mt-2">{voucherLimitError}</div>
          )}
        </div>
      ) : null}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.startDate?.message}
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, onBlur, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              defaultValue={value}
              onChange={onChange}
              error={form.formState.errors.endDate?.message}
            />
          )}
        />
      </div>

      <div className="mb-4">
        <Controller
          control={form.control}
          name="groupLevel"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              defaultValue={TRANSACTION_REPORT_CALENDAR_UNIT.find(
                (i) => i.value === String(value)
              )}
              title="Group By"
              options={TRANSACTION_REPORT_CALENDAR_UNIT}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      <div className="flex gap-4 justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoading}
        >
          <span className="tracking-wider font-bold">
            {isLoading ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>

        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>

      {isError || (isSuccess && (!reportVoucherRdemptionData?.data || (((reportVoucherRdemptionData.data.voucherRedemptionCounts?.length ?? 0) === 0) && ((reportVoucherRdemptionData.data.voucherIssueCounts?.length ?? 0) === 0)))) ? (
        <div
          ref={resultRef}
          className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
        >
          <div>
            <p className="tracking-wide text-red font-light">
              {`Sorry, there are no items available.`}
            </p>
          </div>
        </div>
      ) : null}

      {isSuccess && reportVoucherRdemptionData?.data ? (() => {
        const issueCounts = reportVoucherRdemptionData.data.voucherIssueCounts || []
        const redemptionCounts = reportVoucherRdemptionData.data.voucherRedemptionCounts || []

        const voucherName = issueCounts.length > 0 ? issueCounts[0].voucherName : 'N/A'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const voucherNameById: Record<number, string> = issueCounts.reduce((acc, it: any) => {
          acc[it.voucherId] = it.voucherName || acc[it.voucherId] || 'N/A'
          return acc
        }, {} as Record<number, string>)

        const redemptionByVoucher = redemptionCounts.reduce<Record<number, number>>((acc, item) => {
          acc[item.voucherId] = (acc[item.voucherId] || 0) + (item.redemptionCount || 0)
          return acc
        }, {})

        const issueByVoucher = issueCounts.reduce<Record<number, number>>((acc, item) => {
          acc[item.voucherId] = (acc[item.voucherId] || 0) + (item.issueCount || 0)
          return acc
        }, {})

        const combinedVoucherIds = Array.from(new Set([
          ...issueCounts.map((i) => i.voucherId),
          ...redemptionCounts.map((r) => r.voucherId),
        ]))

        const csvData = combinedVoucherIds.map((vid) => ({
          voucherId: vid,
          voucherName: voucherNameById[vid] || voucherName,
          issued: issueByVoucher[vid] || 0,
          redeemed: redemptionByVoucher[vid] || 0,
        }))

        return (
          <div
            ref={resultRef}
            className="flex flex-col gap-4 bg-gray-200 p-4 rounded-md"
          >
            <div>
              <p className="font-bold text-lg tracking-wide text-primary">
                Results:
              </p>
              <p className="tracking-wide text-primary font-light">
                The report is ready to view or download.
              </p>
            </div>

            <Button
              type="button"
              // icon={faDownload}
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={() => ExportToCSV(csvData, { filename: 'Voucher-Issues-vs-Redemptions' })}
            >
              Download CSV Report
            </Button>

            <Button
              type="button"
              // icon={faDownload}
              className="ml-1 rounded-lg tracking-wide font-bold"
              iconRight={false}
              onClick={handleView}
            >
              View Report
            </Button>
          </div>
        )
      })() : null}
    </form>
  )
}
